// Flow types automatically removed

import { useState, useEffect } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { useIntl, FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import { TRADE_FIRST_WELCOME_PROMO_CODE } from 'dibs-buyer-layout/exports/tradePromoConstants';
import { Link } from 'dibs-elements/exports/Link';
import { Spinner } from 'dibs-elements/exports/Spinner';
const SHIPPING_PROMO_REFERENCE = 'FIRSTDIBS_PROMOTION_SHIPPING_DISCOUNT';

function getUserCurrencyAmount(financialAmount, userCurrency) {
    if (!financialAmount) {
        return {
            amount: null,
            currency: null,
        };
    }

    let amount = financialAmount.amount;
    let currency = financialAmount.currency;

    if (financialAmount.currency === userCurrency) {
        return {
            amount,
            currency,
        };
    }

    const convertedAmountList = financialAmount.convertedAmountList;

    if (convertedAmountList && Array.isArray(convertedAmountList)) {
        const convertedAmount = convertedAmountList.find(
            _amount => (_amount || {}).currency === userCurrency
        );

        if (convertedAmount) {
            amount = convertedAmount.amount;
            currency = convertedAmount.currency;
        }
    }

    return {
        amount,
        currency,
    };
}

const DisplayPromoCode = props => {
    const {
        viewer,
        user,
        promotion,
        item,
        onPromoCodeRedeem,
        onPromoCodeFetch,
        showDetails = true,
        hasPromo,
        hasError,
    } = props;
    const intl = useIntl();
    const [promoCodeApplied, setPromoCodeApplied] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
        setIsLoading(false);
    }, [hasPromo, hasError]);
    useEffect(() => {
        if (onPromoCodeFetch && !hasPromo && promotion?.code) {
            onPromoCodeFetch(promotion.code);
        }
    }, [hasPromo, promotion, onPromoCodeFetch]);
    const { isSold, isOnHoldForDifferentBuyer, pricing } = item || {};
    const { retailPrice, salePrice, netPrice } = pricing || {};

    if (isSold || isOnHoldForDifferentBuyer) {
        return null;
    }

    if (!promotion) {
        return null;
    }

    let discount;
    const { promoCodeMessaging } = viewer || {};
    const {
        buyerDisplayable,
        promotionMeta,
        code,
        percentAdjustmentRate,
        flatAdjustmentRate,
        adjustmentRateList,
        priceRange = [],
    } = promotion || {};
    const { displayMessage, displayCta } = promotionMeta || {};
    const isCustomPromoMesage = promoCodeMessaging && buyerDisplayable;

    if (!code) {
        return null;
    }

    const percentageAmount = percentAdjustmentRate?.rate;
    const userCurrency = props.currency || user?.preferences?.currency || 'USD';

    if (percentageAmount || percentageAmount === 0) {
        discount = intl.formatNumber(percentageAmount / 100, {
            style: 'percent',
        });
    } else {
        const rate = flatAdjustmentRate?.rate;
        const { amount, currency } = getUserCurrencyAmount(rate, userCurrency);

        if ((amount || amount === 0) && currency) {
            discount = intl.formatNumber(amount, {
                style: 'currency',
                currency: currency,
            });
        }
    }

    if (!discount) {
        return null;
    }

    const extraOff = props.isFurtherReduced ? (
        <FormattedMessage id="DisplayPromoCode.extraOff" defaultMessage="Extra " />
    ) : (
        ''
    );
    const promoReference = adjustmentRateList?.[0]?.reference;
    const promoType =
        promoReference === SHIPPING_PROMO_REFERENCE ? (
            <FormattedMessage id="DisplayPromoCode.shippingPromo" defaultMessage="shipping " />
        ) : (
            ''
        );
    let itemPrice = getUserCurrencyAmount(retailPrice, userCurrency);
    const itemSalePrice = getUserCurrencyAmount(salePrice, userCurrency);
    const itemNetPrice = getUserCurrencyAmount(netPrice, userCurrency);

    if (itemNetPrice.amount) {
        itemPrice = itemNetPrice;
    } else if (itemSalePrice.amount) {
        itemPrice = itemSalePrice;
    }

    const minAmount = getUserCurrencyAmount(
        (priceRange?.find(range => range && range.minAmount) || {}).minAmount,
        userCurrency
    );
    const maxAmount = getUserCurrencyAmount(
        (priceRange?.find(range => range && range.maxAmount) || {}).maxAmount,
        userCurrency
    );
    let priceDisplay = '';

    if (
        itemPrice.amount &&
        minAmount.amount &&
        minAmount.currency &&
        minAmount.amount > itemPrice.amount
    ) {
        priceDisplay = (
            <FormattedMessage
                id="common.DisplayPromoCode.belowMinimum"
                defaultMessage={' on items above {amount}'}
                values={{
                    amount: (
                        <FormattedNumber
                            value={minAmount.amount}
                            style="currency"
                            currency={minAmount.currency}
                        />
                    ),
                }}
            />
        );
    } else if (
        itemPrice.amount &&
        maxAmount.amount &&
        maxAmount.currency &&
        maxAmount.amount < itemPrice.amount
    ) {
        priceDisplay = (
            <FormattedMessage
                id="common.DisplayPromoCode.aboveMaximum"
                defaultMessage={' on items below {amount}'}
                values={{
                    amount: (
                        <FormattedNumber
                            value={maxAmount.amount}
                            style="currency"
                            currency={maxAmount.currency}
                        />
                    ),
                }}
            />
        );
    }

    const togglePromoCode = () => {
        if (!isLoading) {
            setPromoCodeApplied(!promoCodeApplied);

            if (promoCodeApplied) {
                onPromoCodeRedeem();
            } else {
                onPromoCodeRedeem(code);
            }
        }

        setIsLoading(true);
    };

    let displayCode = (
        <FormattedMessage
            id="common.DisplayPromoCode.activePromotion.pdpCart"
            defaultMessage={'{extraOff}{discount} off {promoType}with code {code}{priceDisplay}'}
            values={{
                extraOff,
                discount,
                code,
                priceDisplay,
                promoType,
            }}
        />
    );

    if (code === TRADE_FIRST_WELCOME_PROMO_CODE) {
        displayCode = (
            <FormattedMessage
                id="common.DisplayPromoCode.activePromotion.pdpCart.tradeFirstWelcome"
                defaultMessage="Get {points} extra points with code {code}."
                values={{
                    code,
                    points: (
                        <FormattedNumber
                            value={5000}
                            id="common.DisplayPromoCode.activePromotion.pdpCart.tradeFirstWelcome.points"
                        />
                    ),
                }}
            />
        );
    }

    if (isCustomPromoMesage && displayMessage) {
        displayCode = displayMessage.replace(/\{discount\}/, discount);
    }

    const detailsCta =
        isCustomPromoMesage && displayCta ? (
            displayCta
        ) : (
            <FormattedMessage
                id="common.DisplayPromoCode.seeDetails"
                defaultMessage="See Details"
            />
        );
    const seeDetails = props.hideDetails ? null : (
        <Link href={`/promotions/terms/?promo=${code}`} dataTn="promo-details-link" target="_blank">
            {detailsCta}
        </Link>
    );
    const content = props.displaySingleLine ? (
        <div className={props.containerClass} data-tn="promo-code">
            <span className={props.promoDisplayClass}>{displayCode}</span>{' '}
            <span className={props.seeDetailsClass}>{seeDetails}</span>
        </div>
    ) : (
        <div className={props.containerClass} data-tn="promo-code">
            <div className={props.promoDisplayClass}>
                <span className={props.promoCodeClass}>{displayCode}</span>
                {onPromoCodeRedeem && (
                    <Link onClick={() => togglePromoCode()} className={props.redeemPromoCodeClass}>
                        {isLoading && (
                            <Spinner dark containerClass={props.redeemPromoCodeSpinnerClass} />
                        )}
                        {hasPromo ? (
                            <FormattedMessage
                                id="common.DisplayPromoCode.remove"
                                defaultMessage="Remove"
                            />
                        ) : (
                            <FormattedMessage
                                id="common.DisplayPromoCode.redeem"
                                defaultMessage="Redeem"
                            />
                        )}
                    </Link>
                )}
            </div>
            {showDetails && <div className={props.seeDetailsClass}>{seeDetails}</div>}
        </div>
    );
    return content;
};

export { DisplayPromoCode };
export default createFragmentContainer(DisplayPromoCode, {
    viewer: graphql`
        fragment DisplayPromoCode_viewer on Viewer {
            promoCodeMessaging: featureFlag(feature: "promoCodeMessaging")
        }
    `,
    user: graphql`
        fragment DisplayPromoCode_user on User {
            preferences {
                currency
            }
        }
    `,
    promotion: graphql`
        fragment DisplayPromoCode_promotion on Promotion {
            buyerDisplayable
            adjustmentRateList {
                reference
            }
            code
            flatAdjustmentRate {
                rate {
                    currency
                    amount
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                eligibleAmount {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
            }
            percentAdjustmentRate {
                rate
            }
            priceRange: ruleList {
                ... on PriceRangeRule {
                    minAmount {
                        currency
                        amount
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                    maxAmount {
                        currency
                        amount
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                }
            }
            promotionMeta {
                displayMessage
                displayCta
            }
        }
    `,
    item: graphql`
        fragment DisplayPromoCode_item on Item
        @argumentDefinitions(userCountryCode: { type: "String" }) {
            isOnHoldForDifferentBuyer(currentBuyerId: $userId) @include(if: $hasUserId)
            isSold
            serviceId
            pricing(userCountryCode: $userCountryCode) {
                retailPrice {
                    amount
                    currency
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                salePrice {
                    amount
                    currency
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                netPrice {
                    amount
                    currency
                    convertedAmountList {
                        amount
                        currency
                    }
                }
            }
        }
    `,
});
